import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { safelyOpenURL } from 'app/lib/navigation';
import type { Message as MessageType, ChatUser } from 'app/types/chat';
import { RED_HEART, THUMBS_UP } from 'app/network/chat/message';
import {
  CustomMessageType,
  isConciergeMessage,
  isFileMessage,
  isSystemMessage,
  isUserMessage,
  MessageKind,
} from 'app/types/chat';
import type { ComponentProps, RefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileMessage } from './file-message';
import { UserMessage } from './user-message';
import { useTranslation } from 'react-i18next';
import type { DimensionValue } from 'react-native';
import { ActivityIndicator, Modal, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { LoanMessage } from 'app/screens/chat/one-channel/components/loan-message';
import { Rating } from 'app/screens/chat/one-channel/components/rating';
import { useNavigation } from 'app/hooks/navigation';
import { Button } from 'app/design/primitives/button';
import { LoanManagement } from 'app/screens/chat/one-channel/components/loan-management';
import { BonificationMessage } from 'app/screens/chat/one-channel/components/bonification-message';
import { LoanETA } from 'app/types/loan';
import { navigateToListing, navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import type { LoanFeeModalsState } from 'app/screens/management/loan-fee';
import type { FlashOrFlatList } from 'app/design/primitives/page/list';
import { ConciergeMessage } from './concierge-message';
import { type ListingMatchStatus } from 'app/types/listing';
import { Signal } from 'app/design/icons/signal';
import { UnavailabilityMessage } from './unavailability-message';
import { TransportMessage } from './transport-message';
import { PostSaleListingMessageCard } from './post-sale-listing-message';
import { FirstLoanMessageCard } from './first-loan-message';
import { MessageMenu } from './message-menu';
import * as Clipboard from 'expo-clipboard';
import { EmojiReactionBar } from './emoji-reaction-bar';
import { ReplyQuote } from './reply-preview';
import { useDeleteMessage, type RefetchOneChannel } from 'app/network/chat/channel';
import { isMobileNative } from 'app/utils/web-helpers';
import RNHapticFeedback from 'react-native-haptic-feedback';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getSharedListingIdFromAppUrl, getSharedUserProfileIdFromAppUrl } from 'app/utils/helpers';
import type { Theme } from 'app/shopify';
import { IconDots } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { TransportListingMessage } from './transport-listing-message';
import { LoanReciprocityMessageCard } from './loan-reciprocity-message';

function MoreOptions({ onPress, isActive }: { onPress: () => void; isActive: boolean }) {
  const theme = useTheme<Theme>();
  const [hovered, setHovered] = useState(false);
  const { t } = useTranslation('one-channel');

  return (
    <Pressable
      onPress={onPress}
      hitSlop={4}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityLabel={t('chat.messageActions', { ns: 'accessibility' })}
      style={{
        padding: 4,
        borderRadius: 999,
        opacity: isActive ? 1 : hovered ? 0.9 : 0.7,
        backgroundColor: hovered ? theme.colors['surface.tertiary-neutral'] : 'transparent',
      }}
    >
      <IconDots strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.primary']} />
    </Pressable>
  );
}

interface MessageProps extends ComponentProps<typeof Column> {
  isAuthor?: boolean;
  message: MessageType;
  isLender?: boolean;
  isLoanSuperseded?: boolean;
  isLoading?: boolean;
  isLastViewed?: boolean;
  interlocutor?: ChatUser;
  listRef?: RefObject<FlashOrFlatList<MessageType, null>>;
  loanFeeModalsState: LoanFeeModalsState;
  needMatchStatus?: ListingMatchStatus;
  listingId?: string;
  isAutomaticMessage?: boolean;
  isWhoMessage?: boolean;
  showReactionBar?: boolean;
  setShowReactionBar?: React.Dispatch<React.SetStateAction<boolean>>;
  setMessageIndex?: React.Dispatch<React.SetStateAction<number | null>>;
  messageIndex?: number | null;
  index: number;
  toggleReaction: (args: { channelUrl: string; messageId: string; emoji: string }) => void;
  opacity?: number;
  handleReactionSelect: (emoji: string) => void;
  onReply?: () => void;
  onEdit?: () => void;
  onScrollToReply?: (messageId: string) => void;
  currentUserId?: string;
  isGroup?: boolean;
  isTransportSupportChannel?: boolean;
  refetchChannel: RefetchOneChannel;
  variant?: {
    author: 'current-user' | 'other';
    loanRole: 'lender' | 'borrower';
    delivery: 'loading' | 'sent';
    viewState: 'last-viewed' | 'standard';
    origin: 'automatic' | 'support-who' | 'user';
    actionBar: 'visible' | 'hidden';
    conversation: 'group' | 'direct';
    channel: 'transport-support' | 'standard';
  };
}

type ResolvedMessageProps = MessageProps &
  Required<
    Pick<
      MessageProps,
      | 'isAuthor'
      | 'isLender'
      | 'isLastViewed'
      | 'isLoading'
      | 'isAutomaticMessage'
      | 'isWhoMessage'
      | 'showReactionBar'
      | 'isGroup'
      | 'isTransportSupportChannel'
    >
  >;

interface ActionAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function Message(props: MessageProps) {
  const { variant, ...messageProps } = props;
  return (
    <MessageContent
      {...messageProps}
      isAuthor={props.isAuthor ?? variant?.author === 'current-user'}
      isLender={props.isLender ?? variant?.loanRole === 'lender'}
      isLastViewed={props.isLastViewed ?? variant?.viewState === 'last-viewed'}
      isLoading={props.isLoading ?? variant?.delivery === 'loading'}
      isAutomaticMessage={props.isAutomaticMessage ?? variant?.origin === 'automatic'}
      isWhoMessage={props.isWhoMessage ?? variant?.origin === 'support-who'}
      showReactionBar={props.showReactionBar ?? variant?.actionBar === 'visible'}
      isGroup={props.isGroup ?? variant?.conversation === 'group'}
      isTransportSupportChannel={props.isTransportSupportChannel ?? variant?.channel === 'transport-support'}
    />
  );
}

function MessageContent({
  isAuthor,
  message,
  isLender,
  isLoanSuperseded = false,
  isLastViewed,
  isLoading,
  interlocutor,
  listRef,
  loanFeeModalsState,
  listingId,
  needMatchStatus,
  isAutomaticMessage,
  isWhoMessage,
  showReactionBar,
  setShowReactionBar,
  messageIndex,
  setMessageIndex,
  index,
  toggleReaction,
  opacity = 1,
  handleReactionSelect,
  onReply,
  onEdit,
  onScrollToReply,
  currentUserId,
  isGroup,
  isTransportSupportChannel,
  refetchChannel,
  ...rest
}: ResolvedMessageProps) {
  const {
    t,
    bubbleWidth,
    setBubbleWidth,
    menuHeight,
    setMenuHeight,
    reactionBarHeight,
    setReactionBarHeight,
    actionAnchor,
    bubbleContainerRef,
    setContentVisibility,
    hasVisibleContent,
    canShowMenu,
    dismissActions,
    handleLongPress,
    handleDoubleTap,
    measureActionAnchor,
  } = useMessageInteractions({
    message,
    isLoading,
    messageIndex,
    index,
    showReactionBar,
    setShowReactionBar,
    setMessageIndex,
    toggleReaction,
  });

  const theme = useTheme<Theme>();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const safeAreaInsets = useSafeAreaInsets();
  const { mutate } = useDeleteMessage({
    onSettled: () => {
      dismissActions();
    },
  });

  const { backgroundColor, messageWidth, visibleOnlyToYou } = useMessageBubbleAppearance({
    message,
    isAuthor,
    isTransportSupportChannel,
    theme,
    windowWidth,
  });

  const hasReactions = (message.reactions?.length ?? 0) > 0;
  const currentUserReactionEmojis = useMemo(
    () =>
      currentUserId
        ? message.reactions?.reduce<string[]>((emojis, reaction) => {
            if (reaction.userIds.includes(currentUserId)) {
              emojis.push(reaction.emoji);
            }
            return emojis;
          }, [])
        : [],
    [currentUserId, message.reactions],
  );

  const onCopy = useCallback(async () => {
    if (message.type === MessageKind.Message && typeof message.message === 'string') {
      dismissActions();
      await Clipboard.setStringAsync(message.message);
    }
  }, [dismissActions, message]);

  const extraSpace = hasReactions ? theme.spacing['spacing.general.x0,5'] : theme.spacing['spacing.general.x2'];

  const messageContent = (
    <MessageBody
      message={message}
      isLender={isLender}
      isLoanSuperseded={isLoanSuperseded}
      listRef={listRef}
      loanFeeModalsState={loanFeeModalsState}
      refetchChannel={refetchChannel}
      interlocutor={interlocutor}
      listingId={listingId}
      needMatchStatus={needMatchStatus}
      isAuthor={isAuthor}
      isTransportSupportChannel={isTransportSupportChannel}
      onLongPress={handleLongPress}
    />
  );

  const showActions = (showReactionBar && messageIndex === index) || false;
  const {
    screenMargin,
    maxOverlayBubbleHeight,
    overlayBubbleTop,
    overlayLeft,
    reactionBarTop,
    actionMenuWidth,
    menuTop,
  } = getMessageOverlayLayout(
    theme,
    message,
    isAuthor,
    menuHeight,
    reactionBarHeight,
    safeAreaInsets,
    windowHeight,
    bubbleWidth,
    windowWidth,
    actionAnchor,
    extraSpace,
  );

  useEffect(() => {
    if (showActions) {
      measureActionAnchor();
    }
  }, [measureActionAnchor, showActions]);

  // Messages we use to show a badge + send a push notif, but that we don't want to display
  if (message.is_invisible) {
    return null;
  }

  if (message.is_deleted) {
    return <DeletedMessage isAuthor={isAuthor} t={t} />;
  }

  // Only show system messages to the recipient, not the sender
  if (isSystemMessage(message) && isAuthor) {
    return null;
  }

  if (!hasVisibleContent) return null;
  return (
    <Column
      collapsable={false}
      opacity={opacity}
      style={{
        marginBottom:
          theme.spacing['spacing.general.x2'] +
          (hasReactions || message.is_edited ? theme.spacing['spacing.general.x4'] : 0),
      }}
    >
      <Column>
        <MessageReply
          message={message}
          bubbleWidth={bubbleWidth}
          onScrollToReply={onScrollToReply}
          isAuthor={isAuthor}
        />
        <MessageBubble
          canShowMenu={canShowMenu}
          isAuthor={isAuthor}
          bubbleWidth={bubbleWidth}
          handleDoubleTap={handleDoubleTap}
          handleLongPress={handleLongPress}
          showActions={showActions}
          rest={rest}
          bubbleContainerRef={bubbleContainerRef}
          backgroundColor={backgroundColor}
          theme={theme}
          messageWidth={messageWidth}
          setBubbleWidth={setBubbleWidth}
          isGroup={isGroup}
          message={message}
          setContentVisibility={setContentVisibility}
          messageContent={messageContent}
          hasReactions={hasReactions}
          t={t}
        />
        {visibleOnlyToYou ? (
          <Text color="text.placeholder" fontSize={11} marginTop="spacing.general.x2" textAlign="right">
            {t('visibleOnlyToYou')}
          </Text>
        ) : null}
        <MessageReactionPills
          hasReactions={hasReactions}
          windowWidth={windowWidth}
          screenMargin={screenMargin}
          isAuthor={isAuthor}
          bubbleWidth={bubbleWidth}
          message={message}
          toggleReaction={toggleReaction}
          currentUserId={currentUserId}
          theme={theme}
        />
      </Column>

      <MessageActionsOverlay
        showActions={showActions}
        actionAnchor={actionAnchor}
        dismissActions={dismissActions}
        maxOverlayBubbleHeight={maxOverlayBubbleHeight}
        overlayBubbleTop={overlayBubbleTop}
        message={message}
        bubbleWidth={bubbleWidth}
        onScrollToReply={onScrollToReply}
        isAuthor={isAuthor}
        rest={rest}
        backgroundColor={backgroundColor}
        theme={theme}
        isGroup={isGroup}
        messageContent={messageContent}
        visibleOnlyToYou={visibleOnlyToYou}
        t={t}
        overlayLeft={overlayLeft}
        reactionBarTop={reactionBarTop}
        actionMenuWidth={actionMenuWidth}
        setReactionBarHeight={setReactionBarHeight}
        currentUserReactionEmojis={currentUserReactionEmojis}
        handleReactionSelect={handleReactionSelect}
        menuTop={menuTop}
        extraSpace={extraSpace}
        setMenuHeight={setMenuHeight}
        onCopy={onCopy}
        mutate={mutate}
        onReply={onReply}
        onEdit={onEdit}
      />

      <AutomaticMessageLabel
        isAutomaticMessage={isAutomaticMessage}
        isWhoMessage={isWhoMessage}
        isAuthor={isAuthor}
        theme={theme}
        t={t}
      />

      {isLoading ? (
        <Row
          alignSelf="flex-end"
          marginTop="spacing.general.x2"
          style={{ marginBottom: theme.spacing['spacing.general.x4'] }}
        >
          <ActivityIndicator size={13} />
        </Row>
      ) : null}

      <MessageReadReceipt
        showActions={showActions}
        isLastViewed={isLastViewed}
        message={message}
        interlocutor={interlocutor}
        isAutomaticMessage={isAutomaticMessage}
        hasReactions={hasReactions}
        t={t}
      />
    </Column>
  );
}

const styles = StyleSheet.create({
  actionsModalRoot: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0, 0, 0, 0.52)',
  },
  overlayActions: {
    elevation: 3,
    position: 'absolute',
    zIndex: 3,
  },
  overlayBubble: {
    elevation: 2,
    overflow: 'hidden',
    position: 'absolute',
    zIndex: 2,
  },
  actionsBackdrop: {
    zIndex: 1,
  },
  reactionsOverlay: {
    bottom: -14,
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 4,
    position: 'absolute',
    zIndex: 2,
  },
  reactionPill: {
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 2,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  reactionEmoji: {
    fontSize: 14,
    lineHeight: 16,
  },
  reactionCount: {
    color: '#1F2635',
    fontSize: 11,
    fontWeight: '600',
    lineHeight: 13,
  },
});

function MessageReply({
  message,
  bubbleWidth,
  onScrollToReply,
  isAuthor,
}: {
  message: MessageType;
  bubbleWidth: number;
  onScrollToReply: ((messageId: string) => void) | undefined;
  isAuthor: boolean;
}): React.ReactNode {
  return message.reply_to ? (
    <ReplyQuote
      width={bubbleWidth}
      replyTo={message.reply_to}
      onPress={() => onScrollToReply?.(message.reply_to!.message_id)}
      alignSelf={isAuthor ? 'flex-end' : 'flex-start'}
    />
  ) : null;
}

function DeletedMessage({
  isAuthor,
  t,
}: {
  isAuthor: boolean;
  t: ReturnType<typeof useTranslation<'one-channel'>>['t'];
}) {
  return (
    <Column
      marginBottom="spacing.general.x2"
      padding="spacing.general.x2"
      backgroundColor="surface.inverse.tertiary-neutral"
      borderRadius="radius.lg"
      alignSelf={isAuthor ? 'flex-end' : 'flex-start'}
    >
      <Text color="text.placeholder" fontStyle="italic">
        {t(`deletedMessage.${isAuthor ? 'author' : 'other'}`)}
      </Text>
    </Column>
  );
}

function MessageBubble({
  canShowMenu,
  isAuthor,
  bubbleWidth,
  handleDoubleTap,
  handleLongPress,
  showActions,
  rest,
  bubbleContainerRef,
  backgroundColor,
  theme,
  messageWidth,
  setBubbleWidth,
  isGroup,
  message,
  setContentVisibility,
  messageContent,
  hasReactions,
  t,
}: {
  canShowMenu: boolean;
  isAuthor: boolean;
  bubbleWidth: number;
  handleDoubleTap: () => void;
  handleLongPress: () => void;
  showActions: boolean;
  rest: Omit<ComponentProps<typeof Column>, 'isAuthor'>;
  bubbleContainerRef: RefObject<View | null>;
  backgroundColor: string;
  theme: Theme;
  messageWidth: { width: DimensionValue; maxWidth?: undefined } | { maxWidth: DimensionValue; width?: undefined };
  setBubbleWidth: React.Dispatch<React.SetStateAction<number>>;
  isGroup: boolean;
  message: MessageType;
  setContentVisibility: React.Dispatch<React.SetStateAction<{ messageId: string; visible: boolean }>>;
  messageContent: React.JSX.Element;
  hasReactions: boolean;
  t: ReturnType<typeof useTranslation<'one-channel'>>['t'];
}) {
  // Keep a real layout container on web so the absolutely-positioned overflow
  // button is anchored to the current bubble instead of the whole reply block.
  const Wrapper = isMobileNative ? Pressable : View;
  const wrapperProps = isMobileNative
    ? {
        onLongPress: handleLongPress,
        onPress: handleDoubleTap,
        delayLongPress: 300,
      }
    : {};
  return (
    <Wrapper {...wrapperProps}>
      {!isMobileNative && canShowMenu ? (
        <View
          style={{
            width: 30,
            alignItems: 'center',
            position: 'absolute',
            right: isAuthor ? bubbleWidth : undefined,
            left: isAuthor ? undefined : bubbleWidth,
            top: 0,
            bottom: 0,
            justifyContent: 'center',
          }}
        >
          <MoreOptions onPress={handleLongPress} isActive={showActions} />
        </View>
      ) : null}
      <Column
        {...rest}
        innerRef={bubbleContainerRef}
        overflow="hidden"
        style={{
          backgroundColor,
          borderRadius: theme.spacing['spacing.general.x4'],
        }}
        {...messageWidth}
      >
        <Column
          padding={
            message.type === MessageKind.Message && message.custom_type === CustomMessageType.FirstLoanPrompt
              ? undefined
              : 'spacing.general.x2'
          }
          onLayout={(e) => {
            setBubbleWidth(e.nativeEvent.layout.width);
          }}
        >
          {isGroup && !isAuthor && message.user?.nickname ? (
            <Text fontWeight="600" fontSize={12}>
              {message.user.nickname}
            </Text>
          ) : null}
          <View
            collapsable={false}
            onLayout={(event) =>
              setContentVisibility({
                messageId: message.message_id,
                visible: event.nativeEvent.layout.height > 0,
              })
            }
          >
            {messageContent}
          </View>
        </Column>
      </Column>
      <EditedMessageLabel message={message} isAuthor={isAuthor} bubbleWidth={bubbleWidth} t={t} />
    </Wrapper>
  );
}

function EditedMessageLabel({
  message,
  isAuthor,
  bubbleWidth,
  t,
}: {
  message: MessageType;
  isAuthor: boolean;
  bubbleWidth: number;
  t: ReturnType<typeof useTranslation<'one-channel'>>['t'];
}) {
  const theme = useTheme<Theme>();
  return message.is_edited ? (
    <Text
      color="text.placeholder"
      fontSize={11}
      variant="bodyBase"
      style={{
        position: 'absolute',
        top: '100%',
        marginTop: theme.spacing['spacing.general.x0,5'],
        ...(isAuthor ? { right: 0, width: bubbleWidth || undefined } : { left: 0 }),
      }}
    >
      {t('edited')}
    </Text>
  ) : null;
}

function AutomaticMessageLabel({
  isAutomaticMessage,
  isWhoMessage,
  isAuthor,
  theme,
  t,
}: {
  isAutomaticMessage: boolean;
  isWhoMessage: boolean;
  isAuthor: boolean;
  theme: Theme;
  t: ReturnType<typeof useTranslation<'one-channel'>>['t'];
}): React.ReactNode {
  return isAutomaticMessage && !isWhoMessage ? (
    <Row alignItems="center" alignSelf={isAuthor ? 'flex-end' : 'flex-start'} marginTop="spacing.general.x2">
      <View
        style={{
          backgroundColor: theme.colors['surface.tertiary'],
          borderRadius: 999,
          height: 16,
          width: 16,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Signal height={10} width={10} color={theme.colors['text.primary']} />
      </View>

      <Text color="text.placeholder" fontSize={12} marginLeft="spacing.general.x1" style={{ opacity: 0.8 }}>
        {t('automaticMessageLabel')}
      </Text>
    </Row>
  ) : null;
}

function MessageReadReceipt({
  showActions,
  isLastViewed,
  message,
  interlocutor,
  isAutomaticMessage,
  hasReactions,
  t,
}: {
  showActions: boolean;
  isLastViewed: boolean;
  message: MessageType;
  interlocutor: ChatUser | undefined;
  isAutomaticMessage: boolean;
  hasReactions: boolean;
  t: ReturnType<typeof useTranslation<'one-channel'>>['t'];
}): React.ReactNode {
  return !showActions &&
    isLastViewed &&
    message.viewed &&
    interlocutor &&
    !isConciergeMessage(message) &&
    !isAutomaticMessage ? (
    <Text
      color="text.placeholder"
      fontSize={11}
      marginTop={hasReactions ? 'spacing.general.x4' : 'spacing.general.x2'}
      textAlign="right"
      variant="bodyBase"
    >
      {t('message_was_read', { name: interlocutor.firstname })}
    </Text>
  ) : null;
}

function MessageReactionPills({
  hasReactions,
  windowWidth,
  screenMargin,
  isAuthor,
  bubbleWidth,
  message,
  toggleReaction,
  currentUserId,
  theme,
}: {
  hasReactions: boolean;
  windowWidth: number;
  screenMargin: number;
  isAuthor: boolean;
  bubbleWidth: number;
  message: MessageType;
  toggleReaction: (args: { channelUrl: string; messageId: string; emoji: string }) => void;
  currentUserId: string | undefined;
  theme: Theme;
}): React.ReactNode {
  return hasReactions ? (
    <View
      pointerEvents="box-none"
      style={[
        styles.reactionsOverlay,
        {
          maxWidth: Math.max(180, windowWidth - screenMargin * 2),
          ...(isAuthor ? { right: 0 } : { left: 0, width: bubbleWidth || undefined, justifyContent: 'flex-end' }),
        },
      ]}
    >
      {message.reactions?.map((r, idx) => (
        <Pressable
          key={message.message_id + r.emoji + String(idx)}
          onPress={() =>
            toggleReaction({ channelUrl: message.channel_url, messageId: message.message_id, emoji: r.emoji })
          }
          style={[
            styles.reactionPill,
            {
              backgroundColor:
                currentUserId && r.userIds.includes(currentUserId)
                  ? theme.colors['surface.brand.secondary-5.subtle']
                  : theme.colors['surface.tertiary-neutral'],
              borderColor:
                currentUserId && r.userIds.includes(currentUserId)
                  ? theme.colors['surface.inverse.tertiary-neutral']
                  : theme.colors['surface.tertiary-neutral'],
            },
          ]}
        >
          <Text style={styles.reactionEmoji}>{r.emoji}</Text>
          {r.count > 1 ? <Text style={styles.reactionCount}>{r.count}</Text> : null}
        </Pressable>
      ))}
    </View>
  ) : null;
}

function MessageActionsOverlay({
  showActions,
  actionAnchor,
  dismissActions,
  maxOverlayBubbleHeight,
  overlayBubbleTop,
  message,
  bubbleWidth,
  onScrollToReply,
  isAuthor,
  rest,
  backgroundColor,
  theme,
  isGroup,
  messageContent,
  visibleOnlyToYou,
  t,
  overlayLeft,
  reactionBarTop,
  actionMenuWidth,
  setReactionBarHeight,
  currentUserReactionEmojis,
  handleReactionSelect,
  menuTop,
  extraSpace,
  setMenuHeight,
  onCopy,
  mutate,
  onReply,
  onEdit,
}: {
  showActions: boolean;
  actionAnchor: ActionAnchor | null;
  dismissActions: () => void;
  maxOverlayBubbleHeight: number | undefined;
  overlayBubbleTop: number;
  message: MessageType;
  bubbleWidth: number;
  onScrollToReply: ((messageId: string) => void) | undefined;
  isAuthor: boolean;
  rest: Omit<ComponentProps<typeof Column>, 'isAuthor'>;
  backgroundColor: string;
  theme: Theme;
  isGroup: boolean;
  messageContent: React.JSX.Element;
  visibleOnlyToYou: boolean;
  t: ReturnType<typeof useTranslation<'one-channel'>>['t'];
  overlayLeft: number;
  reactionBarTop: number;
  actionMenuWidth: number;
  setReactionBarHeight: React.Dispatch<React.SetStateAction<number>>;
  currentUserReactionEmojis: string[] | undefined;
  handleReactionSelect: (emoji: string) => void;
  menuTop: number;
  extraSpace: number;
  setMenuHeight: React.Dispatch<React.SetStateAction<number>>;
  onCopy: () => Promise<void>;
  mutate: ReturnType<typeof useDeleteMessage>['mutate'];
  onReply: (() => void) | undefined;
  onEdit: (() => void) | undefined;
}) {
  return (
    <Modal
      animationType="none"
      transparent
      visible={showActions ? actionAnchor !== null : false}
      onRequestClose={dismissActions}
    >
      <View style={styles.actionsModalRoot}>
        <Pressable style={[StyleSheet.absoluteFill, styles.actionsBackdrop]} onPress={dismissActions} />

        {actionAnchor ? (
          <View
            pointerEvents="box-none"
            style={[
              styles.overlayBubble,
              {
                left: actionAnchor.x,
                maxHeight: maxOverlayBubbleHeight,
                top: overlayBubbleTop,
                width: actionAnchor.width,
              },
            ]}
          >
            {message.reply_to ? (
              <ReplyQuote
                width={bubbleWidth}
                replyTo={message.reply_to}
                onPress={() => onScrollToReply?.(message.reply_to!.message_id)}
                alignSelf={isAuthor ? 'flex-end' : 'flex-start'}
              />
            ) : null}
            <Column
              {...rest}
              overflow="hidden"
              style={{
                backgroundColor,
                borderRadius: theme.spacing['spacing.general.x4'],
              }}
              width="100%"
            >
              <Column
                padding={
                  message.type === MessageKind.Message && message.custom_type === CustomMessageType.FirstLoanPrompt
                    ? undefined
                    : 'spacing.general.x2'
                }
              >
                {isGroup && !isAuthor && message.user?.nickname ? (
                  <Text fontWeight="600" fontSize={12}>
                    {message.user.nickname}
                  </Text>
                ) : null}
                <View collapsable={false}>{messageContent}</View>
              </Column>
            </Column>
            {visibleOnlyToYou ? (
              <Text color="text.placeholder" fontSize={11} marginTop="spacing.general.x2" textAlign="right">
                {t('visibleOnlyToYou')}
              </Text>
            ) : null}
          </View>
        ) : null}

        <View
          style={[
            styles.overlayActions,
            {
              left: overlayLeft,
              top: reactionBarTop,
              width: actionMenuWidth,
              alignItems: isAuthor ? 'flex-end' : 'flex-start',
            },
          ]}
          onLayout={(e) => setReactionBarHeight(e.nativeEvent.layout.height)}
        >
          <EmojiReactionBar
            currentEmojis={currentUserReactionEmojis}
            onSelect={(emoji) => handleReactionSelect(emoji)}
          />
        </View>

        <View
          style={[
            styles.overlayActions,
            {
              left: overlayLeft,
              top: menuTop + extraSpace,
              width: actionMenuWidth,
            },
          ]}
          onLayout={(e) => setMenuHeight(e.nativeEvent.layout.height)}
        >
          <MessageMenu
            onCopy={() => void onCopy()}
            onDelete={() => mutate({ channelUrl: message.channel_url, messageId: message.message_id })}
            onReply={() => {
              dismissActions();
              onReply?.();
            }}
            onEdit={() => {
              dismissActions();
              onEdit?.();
            }}
            width={actionMenuWidth}
            canCopy={isUserMessage(message)}
            canDelete={isAuthor}
            canEdit={isAuthor ? isUserMessage(message) : false}
          />
        </View>
      </View>
    </Modal>
  );
}

function getMessageOverlayLayout(
  theme: Theme,
  message: MessageType,
  isAuthor: boolean,
  menuHeight: number,
  reactionBarHeight: number,
  safeAreaInsets: ReturnType<typeof useSafeAreaInsets>,
  windowHeight: number,
  bubbleWidth: number,
  windowWidth: number,
  actionAnchor: ActionAnchor | null,
  extraSpace: number,
) {
  const screenMargin = theme.spacing['spacing.general.x4'];
  const overlayGap = theme.spacing['spacing.general.x2'];
  const menuActionCount =
    1 + (isUserMessage(message) ? 1 : 0) + (isAuthor && isUserMessage(message) ? 1 : 0) + (isAuthor ? 1 : 0);
  const measuredMenuHeight = menuHeight || menuActionCount * 48;
  const measuredReactionBarHeight = reactionBarHeight || 56;
  const overlayTopLimit = Math.max(screenMargin, safeAreaInsets.top + screenMargin);
  const overlayBottomLimit = windowHeight - Math.max(screenMargin, safeAreaInsets.bottom + screenMargin);
  const actionMenuWidth = Math.min(Math.max(bubbleWidth, 200), Math.max(200, windowWidth - screenMargin * 2));
  const overlayLeft = actionAnchor
    ? Math.min(
        Math.max(isAuthor ? actionAnchor.x + actionAnchor.width - actionMenuWidth : actionAnchor.x, screenMargin),
        windowWidth - actionMenuWidth - screenMargin,
      )
    : screenMargin;
  const overlayBubbleTop = actionAnchor
    ? (() => {
        const minBubbleTop = overlayTopLimit + measuredReactionBarHeight + overlayGap;
        const maxBubbleTop = overlayBottomLimit - actionAnchor.height - overlayGap - extraSpace - measuredMenuHeight;

        if (maxBubbleTop >= minBubbleTop) {
          return Math.min(Math.max(actionAnchor.y, minBubbleTop), maxBubbleTop);
        }

        return actionAnchor.y > windowHeight / 2 ? maxBubbleTop : minBubbleTop;
      })()
    : screenMargin;
  const maxOverlayBubbleHeight = actionAnchor
    ? Math.max(80, overlayBottomLimit - overlayBubbleTop - overlayGap - extraSpace - measuredMenuHeight)
    : undefined;
  const reactionBarTop = Math.max(overlayTopLimit, overlayBubbleTop - measuredReactionBarHeight - overlayGap);
  const menuTop = actionAnchor
    ? Math.min(
        overlayBubbleTop + Math.min(actionAnchor.height, maxOverlayBubbleHeight ?? actionAnchor.height) + overlayGap,
        overlayBottomLimit - measuredMenuHeight - extraSpace,
      )
    : screenMargin;
  return {
    screenMargin,
    maxOverlayBubbleHeight,
    overlayBubbleTop,
    overlayLeft,
    reactionBarTop,
    actionMenuWidth,
    menuTop,
  };
}

function getMessagePromptVisibility(message: MessageType) {
  const isFullWidthPrompt =
    message.type === MessageKind.Message &&
    (message.custom_type === CustomMessageType.RecomPrompt ||
      message.custom_type === CustomMessageType.LatePrompt ||
      message.custom_type === CustomMessageType.Unavailability ||
      message.custom_type === CustomMessageType.ReturnedPrompt ||
      message.custom_type === CustomMessageType.ConciergePrompt ||
      message.custom_type === CustomMessageType.Loan ||
      message.custom_type === CustomMessageType.BonifyPrompt ||
      message.custom_type === CustomMessageType.TransportPrompt ||
      message.custom_type === CustomMessageType.SalePrompt ||
      message.custom_type === CustomMessageType.PostSaleListingPrompt ||
      message.custom_type === CustomMessageType.LoanReciprocityPrompt ||
      message.custom_type === CustomMessageType.FirstLoanPrompt);

  const visibleOnlyToYou =
    isSystemMessage(message) ||
    (isFullWidthPrompt && ![CustomMessageType.Loan, CustomMessageType.Unavailability].includes(message.custom_type));
  return { isFullWidthPrompt, visibleOnlyToYou };
}

function getSharedMessageTargets(message: MessageType, isTransportSupportChannel: boolean) {
  const sharedListingId =
    message.type === MessageKind.Message && message.og_tag
      ? getSharedListingIdFromAppUrl(message.og_tag['og:url'])
      : undefined;

  const sharedUserProfileId =
    message.type === MessageKind.Message && message.og_tag
      ? getSharedUserProfileIdFromAppUrl(message.og_tag['og:url'])
      : undefined;

  const isInternalShare = Boolean(sharedListingId || sharedUserProfileId);

  const isTransportListingShare =
    isTransportSupportChannel &&
    Boolean(sharedListingId) &&
    message.type === MessageKind.Message &&
    Boolean(message.og_tag);
  return { sharedListingId, sharedUserProfileId, isTransportListingShare, isInternalShare };
}

function useMessageInteractions({
  message,
  isLoading,
  messageIndex,
  index,
  showReactionBar,
  setShowReactionBar,
  setMessageIndex,
  toggleReaction,
}: Pick<
  ResolvedMessageProps,
  | 'message'
  | 'isLoading'
  | 'messageIndex'
  | 'index'
  | 'showReactionBar'
  | 'setShowReactionBar'
  | 'setMessageIndex'
  | 'toggleReaction'
>) {
  const lastTapRef = useRef<number>(0);
  const cooldownRef = useRef(false);
  const bubbleContainerRef = useRef<View>(null);
  const { t } = useTranslation('one-channel');
  const [bubbleWidth, setBubbleWidth] = useState(0);
  const [menuHeight, setMenuHeight] = useState(0);
  const [reactionBarHeight, setReactionBarHeight] = useState(0);
  const [actionAnchor, setActionAnchor] = useState<ActionAnchor | null>(null);
  const [contentVisibility, setContentVisibility] = useState({ messageId: message.message_id, visible: true });
  const hasVisibleContent = contentVisibility.messageId === message.message_id ? contentVisibility.visible : true;

  const canShowMenu = isUserMessage(message) || isFileMessage(message);
  const generateHaptic = useCallback(() => {
    if (isMobileNative) {
      RNHapticFeedback.trigger('impactLight', {
        enableVibrateFallback: true,
        ignoreAndroidSystemSettings: false,
      });
    }
  }, []);

  const measureActionAnchor = useCallback(() => {
    requestAnimationFrame(() => {
      bubbleContainerRef.current?.measureInWindow((x, y, width, height) => {
        setActionAnchor({ x, y, width, height });
      });
    });
  }, []);
  const canHaveReactions = isUserMessage(message) || isFileMessage(message);
  const dismissActions = useCallback(() => {
    setShowReactionBar?.(false);
    requestAnimationFrame(() => setMessageIndex?.(null));
  }, [setMessageIndex, setShowReactionBar]);

  const handleLongPress = useCallback(() => {
    if (isLoading) return;
    if (messageIndex === index && !isMobileNative && showReactionBar) {
      dismissActions();
      return;
    }
    if (!canShowMenu) return;
    generateHaptic();
    measureActionAnchor();
    setMessageIndex?.(index);
    setShowReactionBar?.(true);
  }, [
    isLoading,
    messageIndex,
    index,
    showReactionBar,
    canShowMenu,
    generateHaptic,
    measureActionAnchor,
    setMessageIndex,
    setShowReactionBar,
    dismissActions,
  ]);

  const handleDoubleTap = useCallback(() => {
    if (isLoading || !canHaveReactions || cooldownRef.current) return;
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      generateHaptic();
      setMessageIndex?.(index);
      toggleReaction({ channelUrl: message.channel_url, messageId: message.message_id, emoji: RED_HEART });
      cooldownRef.current = true;
      setTimeout(() => {
        cooldownRef.current = false;
      }, 1000);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  }, [
    isLoading,
    canHaveReactions,
    generateHaptic,
    setMessageIndex,
    index,
    toggleReaction,
    message.channel_url,
    message.message_id,
  ]);

  return {
    t,
    bubbleWidth,
    setBubbleWidth,
    menuHeight,
    setMenuHeight,
    reactionBarHeight,
    setReactionBarHeight,
    actionAnchor,
    bubbleContainerRef,
    setContentVisibility,
    hasVisibleContent,
    canShowMenu,
    dismissActions,
    handleLongPress,
    handleDoubleTap,
    measureActionAnchor,
  };
}

type MessageBodyProps = Pick<
  ResolvedMessageProps,
  | 'message'
  | 'isLender'
  | 'isLoanSuperseded'
  | 'listRef'
  | 'loanFeeModalsState'
  | 'refetchChannel'
  | 'interlocutor'
  | 'listingId'
  | 'needMatchStatus'
  | 'isAuthor'
  | 'isTransportSupportChannel'
> & { onLongPress: () => void };
type TextLikeMessage = Exclude<MessageType, { type: MessageKind.File }>;
type TextMessageBodyProps = Omit<MessageBodyProps, 'message'> & { message: TextLikeMessage };
type OpenGraphTag = NonNullable<TextLikeMessage['og_tag']>;
type PromptMessage = Exclude<
  TextLikeMessage,
  {
    custom_type:
      | CustomMessageType.Loan
      | CustomMessageType.RecomPrompt
      | CustomMessageType.LatePrompt
      | CustomMessageType.ReturnedPrompt
      | CustomMessageType.BonifyPrompt;
  }
>;

function MessageBody({
  message,
  isLender,
  isLoanSuperseded,
  listRef,
  loanFeeModalsState,
  refetchChannel,
  interlocutor,
  listingId,
  needMatchStatus,
  isAuthor,
  isTransportSupportChannel,
  onLongPress,
}: MessageBodyProps) {
  const theme = useTheme<Theme>();
  const textColor = theme.colors['text.primary'];
  if (message.type === MessageKind.Message && message.og_tag) {
    return <SharedMessageCard message={message} isTransportSupportChannel={isTransportSupportChannel} />;
  }
  if (message.type === MessageKind.File) {
    return <FileMessage message={message} onLongPress={onLongPress} />;
  }
  return (
    <LoanFlowMessageBody
      interlocutor={interlocutor}
      isAuthor={isAuthor}
      isLender={isLender}
      isLoanSuperseded={isLoanSuperseded}
      isTransportSupportChannel={isTransportSupportChannel}
      listRef={listRef}
      listingId={listingId}
      loanFeeModalsState={loanFeeModalsState}
      message={message}
      needMatchStatus={needMatchStatus}
      onLongPress={onLongPress}
      refetchChannel={refetchChannel}
      textColor={textColor}
    />
  );
}

function LoanFlowMessageBody({
  interlocutor,
  isAuthor,
  isLender,
  isLoanSuperseded = false,
  listingId,
  listRef,
  loanFeeModalsState,
  message,
  needMatchStatus,
  refetchChannel,
  textColor,
}: TextMessageBodyProps & { textColor: string }) {
  if (message.custom_type === CustomMessageType.Loan) {
    return (
      <LoanMessage
        isLender={isLender}
        isSuperseded={isLoanSuperseded}
        listRef={listRef}
        loanFeeModalsState={loanFeeModalsState}
        message={message}
        textColor={textColor}
        refetchChannel={refetchChannel}
      />
    );
  }
  if (message.custom_type === CustomMessageType.RecomPrompt) {
    return (
      <Rating
        channelUrl={message.channel_url}
        interlocutor={interlocutor?.firstname ?? '-'}
        interlocutorImage={interlocutor?.profile_url}
        isLender={isLender}
        loan={message.loan}
      />
    );
  }
  if (message.custom_type === CustomMessageType.LatePrompt) {
    return <LoanManagement loan={message.loan} loanETA={LoanETA.late} />;
  }
  if (message.custom_type === CustomMessageType.ReturnedPrompt) {
    return <LoanManagement loan={message.loan} loanETA={LoanETA.returned} />;
  }
  if (message.custom_type === CustomMessageType.BonifyPrompt) {
    return <BonificationMessage message={message} listingId={listingId} channelUrl={message.channel_url} />;
  }
  if (message.custom_type === CustomMessageType.ConciergePrompt && listingId) {
    return (
      <ConciergeMessage
        listingId={listingId}
        needMatchStatus={needMatchStatus}
        isLender={isLender}
        interlocutorName={interlocutor?.firstname ?? ''}
        channelUrl={message.channel_url}
      />
    );
  }
  return (
    <PromptMessageBody
      isLender={isLender}
      isAuthor={isAuthor}
      listingId={listingId}
      message={message}
      textColor={textColor}
    />
  );
}

function PromptMessageBody({
  isLender,
  isAuthor,
  listingId,
  message,
  textColor,
}: Pick<TextMessageBodyProps, 'isLender' | 'isAuthor' | 'listingId'> & { message: PromptMessage; textColor: string }) {
  if (message.custom_type === CustomMessageType.Unavailability) {
    return <UnavailabilityMessage message={message} />;
  }
  if (message.custom_type === CustomMessageType.TransportPrompt) {
    return <TransportMessage listingId={listingId} />;
  }
  if (message.custom_type === CustomMessageType.SalePrompt) {
    return <LoanManagement loan={message.loan} />;
  }
  if (message.custom_type === CustomMessageType.PostSaleListingPrompt) {
    return <PostSaleListingMessageCard message={message} />;
  }
  if (message.custom_type === CustomMessageType.LoanReciprocityPrompt) {
    return <LoanReciprocityMessageCard />;
  }
  if (message.custom_type === CustomMessageType.FirstLoanPrompt) {
    return <FirstLoanMessageCard isLender={isLender} message={message} />;
  }
  return <UserMessage isAuthor={isAuthor} message={message} textColor={textColor} />;
}

type SharedMessageCardProps = Pick<ResolvedMessageProps, 'message' | 'isTransportSupportChannel'>;

function SharedMessageCard({ message, isTransportSupportChannel }: SharedMessageCardProps) {
  const { t } = useTranslation('one-channel');
  const theme = useTheme<Theme>();
  const navigation = useNavigation();
  const { width: windowWidth } = useWindowDimensions();
  const { sharedListingId, sharedUserProfileId, isInternalShare } = getSharedMessageTargets(
    message,
    isTransportSupportChannel,
  );
  const openUrl = useCallback(() => {
    if (message.type !== MessageKind.Message) return;

    const url = message.og_tag?.['og:url'];
    if (!url) return;

    if (sharedListingId) {
      navigateToListing(navigation, sharedListingId);
      return;
    }

    if (sharedUserProfileId) {
      navigateToUserProfile(navigation, sharedUserProfileId);
      return;
    }

    return void safelyOpenURL(url);
  }, [message, navigation, sharedListingId, sharedUserProfileId]);

  if (message.type !== MessageKind.Message || !message.og_tag) {
    return null;
  }

  if (isTransportSupportChannel && sharedListingId) {
    return <TransportListingMessage listingId={sharedListingId} ogTag={message.og_tag} onPress={openUrl} />;
  }

  const imageUri = message.og_tag['og:image']?.secure_url || message.og_tag['og:image']?.url;
  const isCompactSharedCard = windowWidth < 520;
  const layout = getSharedMessageCardLayout(Boolean(sharedUserProfileId), isCompactSharedCard);

  return (
    <SharedMessagePreview
      ctaLabel={sharedUserProfileId ? t('seeProfile') : t('seeListing')}
      imageUri={imageUri}
      isInternalShare={isInternalShare}
      layout={layout}
      ogTag={message.og_tag}
      onPress={openUrl}
      sharedListingId={sharedListingId}
      sharedUserProfileId={sharedUserProfileId}
      theme={theme}
    />
  );
}

function getSharedMessageCardLayout(isUserProfile: boolean, isCompact: boolean) {
  return {
    imageSize: isUserProfile ? 50 : 92,
    titleFontSize: isCompact ? 15 : 16,
    descriptionFontSize: isCompact ? 12 : 13,
    descriptionLineHeight: isCompact ? 18 : 19,
    buttonFontSize: isCompact ? 13 : 14,
    flexStyle: isUserProfile
      ? ({ alignSelf: 'flex-start', maxWidth: '100%' } as const)
      : ({ maxWidth: '100%' } as const),
    contentStyle: isUserProfile
      ? ({ flexShrink: 1, minWidth: 0 } as const)
      : ({ flex: 1, flexShrink: 1, minWidth: 0 } as const),
  };
}

function SharedMessagePreview({
  ctaLabel,
  imageUri,
  isInternalShare,
  layout,
  ogTag,
  onPress,
  sharedListingId,
  sharedUserProfileId,
  theme,
}: {
  ctaLabel: string;
  imageUri: string | undefined;
  isInternalShare: boolean;
  layout: ReturnType<typeof getSharedMessageCardLayout>;
  ogTag: OpenGraphTag;
  onPress: () => void;
  sharedListingId: string | undefined;
  sharedUserProfileId: string | undefined;
  theme: Theme;
}) {
  return (
    <Pressable accessibilityRole="link" onPress={onPress} style={layout.flexStyle}>
      <Column
        backgroundColor="surface.default"
        borderRadius="radius.xl"
        gap="spacing.general.x1"
        padding="spacing.general.x2"
        width={sharedUserProfileId ? undefined : '100%'}
        style={{
          borderColor: theme.colors['surface.disabled'],
          borderWidth: 1,
          shadowColor: theme.colors['text.primary'],
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 10,
          elevation: 2,
        }}
      >
        {!isInternalShare ? (
          <Text color="text.placeholder" fontSize={12} numberOfLines={1}>
            {ogTag['og:url']}
          </Text>
        ) : null}

        <Row
          alignItems="stretch"
          gap="spacing.general.x1"
          width={sharedUserProfileId ? undefined : '100%'}
          style={layout.flexStyle}
        >
          <SharedMessageImage
            imageUri={imageUri}
            isUserProfile={Boolean(sharedUserProfileId)}
            layout={layout}
            theme={theme}
          />
          <SharedMessageDetails
            ctaLabel={ctaLabel}
            layout={layout}
            ogTag={ogTag}
            onPress={onPress}
            showCallToAction={Boolean(sharedListingId || sharedUserProfileId)}
            theme={theme}
          />
        </Row>
      </Column>
    </Pressable>
  );
}

function SharedMessageImage({
  imageUri,
  isUserProfile,
  layout,
  theme,
}: {
  imageUri: string | undefined;
  isUserProfile: boolean;
  layout: ReturnType<typeof getSharedMessageCardLayout>;
  theme: Theme;
}) {
  if (!imageUri) return null;
  return (
    <PCImage
      aspectRatio={1}
      source={{ uri: imageUri }}
      style={{
        backgroundColor: theme.colors['surface.tertiary-neutral'],
        borderRadius: isUserProfile ? 999 : theme.borderRadii['radius.md'],
        flexShrink: 0,
        height: layout.imageSize,
        width: layout.imageSize,
      }}
    />
  );
}

function SharedMessageDetails({
  ctaLabel,
  layout,
  ogTag,
  onPress,
  showCallToAction,
  theme,
}: {
  ctaLabel: string;
  layout: ReturnType<typeof getSharedMessageCardLayout>;
  ogTag: OpenGraphTag;
  onPress: () => void;
  showCallToAction: boolean;
  theme: Theme;
}) {
  return (
    <Column
      gap="spacing.general.x1"
      justifyContent={ogTag['og:description'] ? 'space-between' : 'center'}
      style={layout.contentStyle}
    >
      <Column flexShrink={1} gap="spacing.general.x1" style={{ minWidth: 0 }}>
        {ogTag['og:title'] ? (
          <Text
            fontSize={layout.titleFontSize}
            fontWeight="700"
            lineHeight={layout.titleFontSize + 4}
            numberOfLines={2}
            style={{ flexShrink: 1, minWidth: 0 }}
          >
            {ogTag['og:title']}
          </Text>
        ) : null}

        {ogTag['og:description'] ? (
          <Text
            color="text.placeholder"
            fontSize={layout.descriptionFontSize}
            lineHeight={layout.descriptionLineHeight}
            numberOfLines={2}
            style={{ flexShrink: 1, maxWidth: '100%', minWidth: 0 }}
          >
            {ogTag['og:description']}
          </Text>
        ) : null}
      </Column>

      {showCallToAction ? (
        <Button
          onPress={onPress}
          style={{
            backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
            borderRadius: theme.borderRadii['radius.xxl'],
            minHeight: 28,
            paddingHorizontal: theme.spacing['spacing.general.x3'],
            alignSelf: 'flex-start',
            maxWidth: '100%',
          }}
          variant="small-pill"
        >
          <Text color="text.primary" fontSize={layout.buttonFontSize} fontWeight="600" numberOfLines={1}>
            {ctaLabel}
          </Text>
        </Button>
      ) : null}
    </Column>
  );
}

function useMessageBubbleAppearance({
  message,
  isAuthor,
  isTransportSupportChannel,
  theme,
  windowWidth,
}: Pick<ResolvedMessageProps, 'message' | 'isAuthor' | 'isTransportSupportChannel'> & {
  theme: Theme;
  windowWidth: number;
}) {
  const isThumbsUp = message.type === MessageKind.Message && message.message === THUMBS_UP;

  const { sharedListingId, isTransportListingShare } = getSharedMessageTargets(message, isTransportSupportChannel);

  const { isFullWidthPrompt, visibleOnlyToYou } = getMessagePromptVisibility(message);

  const backgroundColor = useMemo(() => {
    if (message.type === MessageKind.Message && message.og_tag) {
      return 'transparent';
    }
    if (isConciergeMessage(message)) {
      return theme.colors['surface.primary-neutral'];
    }
    if (message.type === MessageKind.Message && message.custom_type === CustomMessageType.Loan) {
      return theme.colors['surface.primary-neutral'];
    }
    if (message.type === MessageKind.Message && message.custom_type === CustomMessageType.FirstLoanPrompt) {
      return 'transparent';
    }
    if ((isAuthor || isThumbsUp || isFullWidthPrompt) && !isUserMessage(message)) {
      return theme.colors['surface.primary-neutral'];
    }
    if (isSystemMessage(message)) {
      return theme.colors['surface.primary-neutral'];
    }
    return isAuthor
      ? theme.colors['surface.secondary'] //theme.colors['aqua.light']
      : isThumbsUp
        ? 'transparent'
        : isFileMessage(message)
          ? 'transparent'
          : theme.colors['surface.secondary-neutral']; //theme.colors['accent.lighter'];
  }, [isAuthor, isFullWidthPrompt, isThumbsUp, message, theme.colors]);

  const messageWidth = useMemo(() => {
    if (isFullWidthPrompt) {
      return { width: '100%' as DimensionValue };
    }
    if (isTransportListingShare && windowWidth < 520) {
      return { width: '100%' as DimensionValue };
    }
    return message.type === MessageKind.File || sharedListingId
      ? { width: '75%' as DimensionValue }
      : { maxWidth: '75%' as DimensionValue };
  }, [isFullWidthPrompt, isTransportListingShare, message.type, sharedListingId, windowWidth]);

  return { backgroundColor, messageWidth, visibleOnlyToYou };
}
